import { useCallback, useContext, useEffect, useState } from "react";
import { getCurrentTimezone } from "helpers/time/timezone";
import useRegisterToEvent from "modules/event/hooks/useRegisterToEvent";
import {
  selectUser,
  selectInviteeEmail,
  selectPreferredLoginMethods,
} from "modules/auth/redux/selectors";
import { EVENT_CONTEXT } from "modules/event/eventView/EventViewContext";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { useI18n } from "i18n";
import { useCheckEventStatus } from "modules/event/hooks/useCheckEventStatus";
import { isEventManager, isEventSpeaker } from "modules/event/utils/eventUtils";
import { IS_IN_GOOGLE_AUTH } from "common";
import { useSelector } from "react-redux";
import { useAppDispatch } from "store/hooks";
import { IEvent } from "modules/event/types";
import { showPrefilledPreferredLoginMethods } from "modules/auth/redux/authSlice";
import { useHasAnsweredQuestions } from "modules/registration";
import { usePrevious } from "helpers/reactHooksUtils";
import { setShowRegisterDialog } from "./redux/eventLandingSlice";
import { EventDetails } from "./EventDetails";
import { selectShowRegisterDialog } from "./redux/eventLandingSelectors";
import { useEventLandingPageParameters } from "./hooks/useEventLandingPageParameters";

interface Props {
  eventData: IEvent;
  currentTime: number;
  isEventFull?: boolean;
  setShowCalendarDialog(arg1: boolean): void;
}

const EVENT_OPEN_CHECK_INTERVAL = 5_000;

const EventDetailsContainer = ({ setShowCalendarDialog, ...props }: Props) => {
  const { triggerAnonymousLogin } = useEventLandingPageParameters();
  const dispatch = useAppDispatch();
  const showRegisterDialog = useSelector(selectShowRegisterDialog);
  const inviteeEmail = useSelector(selectInviteeEmail);
  const preferredLoginMethods = useSelector(selectPreferredLoginMethods);
  const { eventData, isEventFull } = props;
  const { eventMember } = useContext(EVENT_CONTEXT);
  const { t } = useI18n(["common", "eventLanding"]);
  const {
    checkIfEventHappeningNow,
    checkIfEventEnded,
    checkIsAllowedToJoinEvent,
  } = useCheckEventStatus();
  const { registerToEvent, isRegistered, goToEventMap } = useRegisterToEvent();
  const { addErrorNotification } = useNotificationActions();
  const user = useSelector(selectUser);

  const timezone = getCurrentTimezone();
  const isEnded = checkIfEventEnded(eventData);
  const isHappeningNow = checkIfEventHappeningNow(eventData);

  const isSpeaker =
    isEventSpeaker(eventData, user?.id, user?.email) ||
    eventMember?.role === "speaker";
  const isManager = isEventManager(eventData, user?.id);
  const showJoinEventButton = isHappeningNow || isSpeaker || isManager;
  const amIRegistered = eventData.id
    ? isRegistered(eventData.id, eventMember)
    : false;
  const isInGoogleAuth =
    window.localStorage.getItem(IS_IN_GOOGLE_AUTH) === "true";
  const isLobbyEnabled = Boolean(eventData.isLobbyActive);
  const [isEventOpen, setIsEventOpen] = useState(
    checkIsAllowedToJoinEvent(eventData),
  );
  const [queuedJoinEventRetry, setQueuedJoinEventRetry] = useState(false);
  const [queuedJoinAsSpeakerRetry, setQueuedJoinAsSpeakerRetry] =
    useState(false);
  const { hasAnsweredQuestions, hasQuestions, showRegistrationForm } =
    useHasAnsweredQuestions(eventData.id);
  const prevHasAnsweredQuestions = usePrevious(hasAnsweredQuestions);

  useEffect(() => {
    if (triggerAnonymousLogin) {
      dispatch(setShowRegisterDialog(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEventOpen) return;

    const interval = setInterval(() => {
      setIsEventOpen(checkIsAllowedToJoinEvent(eventData));
    }, EVENT_OPEN_CHECK_INTERVAL);

    // eslint-disable-next-line consistent-return
    return () => {
      clearInterval(interval);
    };
  }, [isEventOpen, eventData, checkIsAllowedToJoinEvent]);

  useEffect(() => {
    // If this flag is set, user is coming back after google login. Trigger join event now
    if (isInGoogleAuth) {
      window.localStorage.removeItem(IS_IN_GOOGLE_AUTH);
      dispatch(setShowRegisterDialog(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInGoogleAuth]);

  const handleJoinEvent = useCallback(() => {
    if (hasQuestions && !hasAnsweredQuestions) {
      showRegistrationForm();
      setQueuedJoinEventRetry(true);
      return;
    }

    // If event manager is not registered/added to event member, register once here
    if (eventMember || isSpeaker || isManager) {
      goToEventMap(eventData);

      return;
    }

    if (inviteeEmail && preferredLoginMethods.length > 1) {
      dispatch(showPrefilledPreferredLoginMethods());
    }

    dispatch(setShowRegisterDialog(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventData, eventMember, hasAnsweredQuestions, hasQuestions]);

  const handleJoinLobby = useCallback(() => {
    goToEventMap(eventData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleJoinAsSpeaker = useCallback(async () => {
    if (!user) {
      if (inviteeEmail && preferredLoginMethods.length > 1) {
        dispatch(showPrefilledPreferredLoginMethods());
      }

      dispatch(setShowRegisterDialog(true));

      return;
    }

    if (hasQuestions && !hasAnsweredQuestions && !isManager) {
      showRegistrationForm();
      setQueuedJoinAsSpeakerRetry(true);
      return;
    }

    // If event manager is not registered/added to event member, register once here
    if (eventMember && (isSpeaker || isManager)) {
      goToEventMap(eventData);

      return;
    }

    const result = await registerToEvent(eventData.id);

    // navigate to event space when it is speaker
    if ((result && result.role === "speaker") || isManager) {
      window.location.href = `/e/${eventData.code}`;

      return;
    }

    const message = t("eventLanding:not.speaker.mail");

    addErrorNotification({ message });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    eventMember,
    isSpeaker,
    isManager,
    eventData,
    hasAnsweredQuestions,
    hasQuestions,
  ]);

  useEffect(() => {
    if (
      queuedJoinEventRetry &&
      !prevHasAnsweredQuestions &&
      hasAnsweredQuestions
    ) {
      handleJoinEvent();
      setQueuedJoinEventRetry(false);
    }
  }, [
    handleJoinEvent,
    hasAnsweredQuestions,
    prevHasAnsweredQuestions,
    queuedJoinEventRetry,
  ]);

  useEffect(() => {
    if (
      queuedJoinAsSpeakerRetry &&
      !prevHasAnsweredQuestions &&
      hasAnsweredQuestions
    ) {
      handleJoinAsSpeaker();
      setQueuedJoinEventRetry(false);
    }
  }, [
    handleJoinAsSpeaker,
    hasAnsweredQuestions,
    prevHasAnsweredQuestions,
    queuedJoinAsSpeakerRetry,
  ]);

  const onAddToCalendarClick = () => {
    dispatch(setShowRegisterDialog(false));
    setShowCalendarDialog(true);
  };

  return (
    <EventDetails
      {...props}
      isEventFull={isEventFull}
      timezone={timezone}
      amIRegistered={amIRegistered}
      isEnded={isEnded}
      isManager={isManager}
      isSpeaker={isSpeaker}
      showJoinEventButton={showJoinEventButton}
      showRegisterDialog={showRegisterDialog}
      isLobbyEnabled={isLobbyEnabled}
      isEventOpen={isEventOpen}
      handleJoinAsSpeaker={handleJoinAsSpeaker}
      handleJoinEvent={handleJoinEvent}
      handleJoinLobby={handleJoinLobby}
      onAddToCalendarClick={onAddToCalendarClick}
    />
  );
};

export default EventDetailsContainer;
